import React from "react";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import {
  ArrowClockwiseIcon,
  PaperPlaneTiltIcon,
  PencilSimpleLineIcon,
  TrashIcon,
} from "@phosphor-icons/react/dist/ssr";

import type { Problem } from "@/types";
import { htmlContentStyles } from "@/utils/htmlContentStyle";
import { renderMathInHtml } from "@/utils/mathRenderer";

import ProblemAnswerInput from "./ProblemAnswerInput";

interface ViewProblemProps {
  problem: Problem;
  answer: string;
  setAnswer: (val: string) => void;
  canSubmit: boolean;
  loading: boolean;
  handleSubmit: () => void;
  isAnswerValid: () => boolean;
  onApprove?: () => void;
  onEdit?: () => void;
  onRegenerate?: () => void;
  onDelete?: () => void;
}

const ViewProblem: React.FC<ViewProblemProps> = ({
  problem,
  answer,
  setAnswer,
  canSubmit,
  loading,
  handleSubmit,
  isAnswerValid,
  onApprove,
  onEdit,
  onRegenerate,
  onDelete,
}) => (
  <>
    <DialogContent
      dividers
      sx={{
        borderTop: "none",
        borderBottom: "none",
      }}
    >
      <Stack spacing={2.5}>
        <Typography variant="text15B">
          Read the following and answer the question below:
        </Typography>
        <Typography
          dangerouslySetInnerHTML={{
            __html: renderMathInHtml(problem.question, problem.formatVersion),
          }}
          sx={{
            ...htmlContentStyles,
            fontSize: "13px"
          }}
        />

        <ProblemAnswerInput
          problem={problem}
          answer={answer}
          setAnswer={setAnswer}
          showSolution={!!problem.studentAnswer || !canSubmit}
          correctAnswer={problem.correctAnswer}
        />
        {(!!problem.studentAnswer || !canSubmit) && (
          <Stack spacing={1}>
            <Typography variant="text15B">
              Solution
            </Typography>
            <Typography
              variant="body1"
              p={1}
              dangerouslySetInnerHTML={{
                __html: renderMathInHtml(problem.solution, problem.formatVersion),
              }}
              sx={{
                ...htmlContentStyles,
                backgroundColor: "rgba(30, 74, 53, 0.06)",
                border: "1px solid rgba(30, 74, 53, 0.2)",
                borderRadius: 2,
                fontSize: "13px"
              }}
            />
          </Stack>
        )}
      </Stack>
    </DialogContent>
    {!problem?.studentAnswer && canSubmit && (
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isAnswerValid()}
          sx={{
            mt: 2,
            borderRadius: 2,
            px: 2.5,
            textTransform: "none",
            backgroundColor: "#1E4A35",
            "&:hover": {
              backgroundColor: "#163927",
            },
          }}
          loading={loading}
          loadingIndicator="Grading..."
        >
          Submit Answer
        </Button>
      </DialogActions>
    )}
    {!problem?.isApproved && (
      <DialogActions
        sx={{
          justifyContent: "flex-end",
          gap: 0.75,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            justifyContent: "flex-end",
          }}
        >
          <>
            {onApprove && (
              <IconButton
                onClick={() => onApprove?.()}
                size="small"
                sx={{
                  p: 0.5,
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(235, 94, 42, 0.1)",
                  },
                }}
              >
                <PaperPlaneTiltIcon size={16} weight="bold" />
              </IconButton>
            )}
            {onEdit && (
              <IconButton
                onClick={() => onEdit?.()}
                size="small"
                sx={{
                  p: 0.5,
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "rgba(28, 72, 46, 0.08)",
                  },
                }}
              >
                <PencilSimpleLineIcon size={16} weight="bold" />
              </IconButton>
            )}
            {problem.isGenerated && onRegenerate && (
              <IconButton
                onClick={() => onRegenerate?.()}
                size="small"
                sx={{
                  p: 0.5,
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "rgba(28, 72, 46, 0.08)",
                  },
                }}
              >
                <ArrowClockwiseIcon size={16} weight="bold" />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                onClick={() => onDelete?.()}
                size="small"
                sx={{
                  p: 0.5,
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "rgba(240, 82, 82, 0.1)",
                  },
                }}
              >
                <TrashIcon size={16} weight="bold" />
              </IconButton>
            )}
          </>
        </Box>
      </DialogActions>
    )}
  </>
);

export default ViewProblem;
